import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * Conduit Create Modal
 *
 * Modal for configuring and saving newly drawn conduits.
 * Opens after a user draws a line on the map and it's snapped to structures.
 * Uses centralized modal wrapper and theme system.
 */
import { useState, useCallback, useEffect } from 'react';
import { MapPin, Save, Loader2, AlertTriangle } from 'lucide-react';
import { ModalWrapper, ModalHeader, ModalBody, ModalFooter } from '../../../../components/ui/modal-wrapper';
// Network options based on existing infrastructure patterns
const NETWORK_OPTIONS = [
    { value: 'CFN', label: 'CFN' },
    { value: 'OR', label: 'Openreach (OR)' },
    { value: 'OR_UNDOCUMENTED', label: 'OR Undocumented' },
];
// RAG status options
const RAG_STATUS_OPTIONS = [
    { value: '', label: 'Not Set' },
    { value: 'GREEN', label: 'Green' },
    { value: 'AMBER', label: 'Amber' },
    { value: 'RED', label: 'Red' },
];
// Number of ways options
const NUMBER_OF_WAYS_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
const ConduitCreateModal = ({ isOpen, data, onClose, onSuccess, }) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(null);
    // Form state
    const [network, setNetwork] = useState('CFN');
    const [exchangeCode, setExchangeCode] = useState('');
    const [numberOfWays, setNumberOfWays] = useState(1);
    const [ragStatus, setRagStatus] = useState('');
    const [sectionNumber, setSectionNumber] = useState('');
    // Reset form when modal opens with new data
    useEffect(() => {
        if (isOpen && data) {
            setNetwork('CFN');
            setExchangeCode('');
            setNumberOfWays(1);
            setRagStatus('');
            setSectionNumber('');
            setError(null);
        }
    }, [isOpen, data]);
    // Close on escape
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape' && isOpen) {
                handleCancel();
            }
        };
        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [isOpen]);
    const handleCancel = useCallback(() => {
        // Remove the layer from the map when cancelling
        if (data?.layer) {
            data.layer.remove();
        }
        onClose();
    }, [data, onClose]);
    const handleSubmit = useCallback(async (e) => {
        e.preventDefault();
        if (!data) {
            setError('No conduit data available');
            return;
        }
        // Validation
        if (!network) {
            setError('Network is required');
            return;
        }
        setIsSubmitting(true);
        setError(null);
        try {
            const payload = {
                geometry: data.geom27700,
                properties: data.properties,
                network,
                exchange_1141_code: exchangeCode || null,
                number_of_ways: numberOfWays,
                rag_status: ragStatus || null,
                section_number: sectionNumber || null,
            };
            const response = await fetch('/conduit/create', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            });
            const result = await response.json();
            if (result.error) {
                throw new Error(result.error);
            }
            if (result.feature) {
                // Remove the temporary drawn layer
                if (data.layer) {
                    data.layer.remove();
                }
                // Refresh the conduit layer to show new conduit
                if (window.CFN?.getCachedLayer) {
                    const conduitLayer = window.CFN.getCachedLayer('ConduitLayer');
                    if (conduitLayer) {
                        conduitLayer.setParams({ time: Date.now() }, false);
                    }
                }
                // Notify parent of success
                onSuccess?.(result.feature);
                // Dispatch event for map refresh
                window.dispatchEvent(new CustomEvent('cfn:conduit:created', {
                    detail: { feature: result.feature },
                }));
                onClose();
            }
            else {
                throw new Error('Failed to create conduit - no feature returned');
            }
        }
        catch (err) {
            console.error('Failed to create conduit:', err);
            setError(err instanceof Error ? err.message : 'Failed to create conduit');
        }
        finally {
            setIsSubmitting(false);
        }
    }, [data, network, exchangeCode, numberOfWays, ragStatus, sectionNumber, onClose, onSuccess]);
    if (!isOpen || !data)
        return null;
    const measuredLength = data.properties.MEASURED_LENGTH?.toFixed(2) || '0.00';
    return (_jsxs(ModalWrapper, { size: "md", children: [_jsx(ModalHeader, { onClose: handleCancel, children: _jsxs("span", { className: "flex items-center gap-2", children: [_jsx(MapPin, { size: 20, className: "text-[var(--cfn-accent-primary)]" }), "Create New Conduit"] }) }), _jsx(ModalBody, { children: _jsxs("form", { id: "conduit-create-form", onSubmit: handleSubmit, noValidate: true, children: [_jsxs("div", { className: "rounded-lg p-4 mb-4", style: {
                                background: 'var(--cfn-info-muted)',
                                border: '1px solid var(--cfn-info)',
                            }, children: [_jsxs("div", { className: "flex justify-between items-center py-1 text-sm border-b", style: { borderColor: 'var(--cfn-border-muted)' }, children: [_jsx("span", { style: { color: 'var(--cfn-text-muted)' }, children: "Length:" }), _jsxs("span", { className: "font-semibold tabular-nums", style: { color: 'var(--cfn-text-primary)' }, children: [measuredLength, " m"] })] }), _jsxs("div", { className: "flex justify-between items-center py-1 text-sm border-b", style: { borderColor: 'var(--cfn-border-muted)' }, children: [_jsx("span", { style: { color: 'var(--cfn-text-muted)' }, children: "Start Structure:" }), _jsx("span", { className: "font-semibold", style: { color: 'var(--cfn-text-primary)' }, children: data.properties.START_STRUCTURE_ID || 'Unknown' })] }), _jsxs("div", { className: "flex justify-between items-center py-1 text-sm", children: [_jsx("span", { style: { color: 'var(--cfn-text-muted)' }, children: "End Structure:" }), _jsx("span", { className: "font-semibold", style: { color: 'var(--cfn-text-primary)' }, children: data.properties.END_STRUCTURE_ID || 'Unknown' })] })] }), _jsxs("div", { className: "space-y-4", children: [_jsxs("div", { className: "flex flex-col gap-1.5", children: [_jsx("label", { htmlFor: "conduit-network", className: "text-sm font-medium", style: { color: 'var(--cfn-text-secondary)' }, children: "Network *" }), _jsx("select", { id: "conduit-network", value: network, onChange: (e) => setNetwork(e.target.value), required: true, className: "w-full px-3 py-2 rounded-md text-sm transition-colors focus:outline-none", style: {
                                                background: 'var(--cfn-input-bg)',
                                                border: '1px solid var(--cfn-input-border)',
                                                color: 'var(--cfn-input-text)',
                                            }, children: NETWORK_OPTIONS.map((opt) => (_jsx("option", { value: opt.value, children: opt.label }, opt.value))) })] }), _jsxs("div", { className: "flex flex-col gap-1.5", children: [_jsx("label", { htmlFor: "conduit-exchange", className: "text-sm font-medium", style: { color: 'var(--cfn-text-secondary)' }, children: "Exchange Code" }), _jsx("input", { id: "conduit-exchange", type: "text", value: exchangeCode, onChange: (e) => setExchangeCode(e.target.value.toUpperCase()), placeholder: "e.g., LNCAM", maxLength: 20, className: "w-full px-3 py-2 rounded-md text-sm transition-colors focus:outline-none", style: {
                                                background: 'var(--cfn-input-bg)',
                                                border: '1px solid var(--cfn-input-border)',
                                                color: 'var(--cfn-input-text)',
                                            } })] }), _jsxs("div", { className: "grid grid-cols-2 gap-4", children: [_jsxs("div", { className: "flex flex-col gap-1.5", children: [_jsx("label", { htmlFor: "conduit-ways", className: "text-sm font-medium", style: { color: 'var(--cfn-text-secondary)' }, children: "Number of Ways" }), _jsx("select", { id: "conduit-ways", value: numberOfWays, onChange: (e) => setNumberOfWays(parseInt(e.target.value, 10)), className: "w-full px-3 py-2 rounded-md text-sm transition-colors focus:outline-none", style: {
                                                        background: 'var(--cfn-input-bg)',
                                                        border: '1px solid var(--cfn-input-border)',
                                                        color: 'var(--cfn-input-text)',
                                                    }, children: NUMBER_OF_WAYS_OPTIONS.map((num) => (_jsx("option", { value: num, children: num }, num))) })] }), _jsxs("div", { className: "flex flex-col gap-1.5", children: [_jsx("label", { htmlFor: "conduit-rag", className: "text-sm font-medium", style: { color: 'var(--cfn-text-secondary)' }, children: "RAG Status" }), _jsx("select", { id: "conduit-rag", value: ragStatus, onChange: (e) => setRagStatus(e.target.value), className: "w-full px-3 py-2 rounded-md text-sm transition-colors focus:outline-none", style: {
                                                        background: 'var(--cfn-input-bg)',
                                                        border: '1px solid var(--cfn-input-border)',
                                                        color: 'var(--cfn-input-text)',
                                                    }, children: RAG_STATUS_OPTIONS.map((opt) => (_jsx("option", { value: opt.value, children: opt.label }, opt.value))) })] })] }), _jsxs("div", { className: "flex flex-col gap-1.5", children: [_jsx("label", { htmlFor: "conduit-section", className: "text-sm font-medium", style: { color: 'var(--cfn-text-secondary)' }, children: "Section Number" }), _jsx("input", { id: "conduit-section", type: "text", value: sectionNumber, onChange: (e) => setSectionNumber(e.target.value), placeholder: "e.g., D1234", maxLength: 50, className: "w-full px-3 py-2 rounded-md text-sm transition-colors focus:outline-none", style: {
                                                background: 'var(--cfn-input-bg)',
                                                border: '1px solid var(--cfn-input-border)',
                                                color: 'var(--cfn-input-text)',
                                            } })] })] }), error && (_jsxs("div", { className: "flex items-center gap-2 p-3 mt-4 rounded-md text-sm", style: {
                                background: 'var(--cfn-danger-muted)',
                                color: 'var(--cfn-danger-text)',
                            }, children: [_jsx(AlertTriangle, { size: 16 }), error] }))] }) }), _jsxs(ModalFooter, { children: [_jsx("button", { type: "button", onClick: handleCancel, disabled: isSubmitting, className: "inline-flex items-center justify-center px-4 py-2 rounded-md text-sm font-medium transition-colors disabled:opacity-50", style: {
                            background: 'var(--cfn-btn-secondary-bg)',
                            border: '1px solid var(--cfn-btn-secondary-border)',
                            color: 'var(--cfn-btn-secondary-text)',
                        }, children: "Cancel" }), _jsx("button", { type: "submit", form: "conduit-create-form", disabled: isSubmitting, className: "inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium transition-colors disabled:opacity-50", style: {
                            background: 'var(--cfn-btn-primary-bg)',
                            border: '1px solid var(--cfn-btn-primary-border)',
                            color: 'var(--cfn-btn-primary-text)',
                        }, children: isSubmitting ? (_jsxs(_Fragment, { children: [_jsx(Loader2, { size: 14, className: "animate-spin" }), "Saving..."] })) : (_jsxs(_Fragment, { children: [_jsx(Save, { size: 14 }), "Save Conduit"] })) })] })] }));
};
export default ConduitCreateModal;
